/**
 * Authentication utilities for the React frontend
 */

// Use relative URL for API requests - this works with the proxy configuration
const API_URL = '/api';

/**
 * Get the current authentication status and user info
 * @returns {Promise<Object>} Authentication status and user info
 */
export const getAuthStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/auth/session`, {
      credentials: 'include', // Important for cookies
    });
    
    if (!response.ok) {
      return { authenticated: false };
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return { authenticated: false };
  }
};

/**
 * Get the current user information (email)
 * @returns {Promise<string|null>} The user's email or null if not authenticated
 */
export const getCurrentUser = async () => {
  try {
    const authStatus = await getAuthStatus();
    return authStatus.authenticated ? authStatus.user.email : null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Login with email and password
 * @param {string} email The user's email
 * @param {string} password The user's password
 * @returns {Promise<Object>} Authentication result
 */
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ email, password }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }
    
    return data;
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

/**
 * Logout the current user
 * @returns {Promise<Object>} Logout result
 */
export const logout = async () => {
  try {
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Logout failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  }
};

/**
 * Change the current user's password
 * @param {string} currentPassword The current password
 * @param {string} newPassword The new password
 * @returns {Promise<Object>} Result of the password change
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await fetch(`${API_URL}/auth/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }
    
    return data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Get all users (admin only)
 * @returns {Promise<Array>} List of users
 */
export const getUsers = async () => {
  try {
    const response = await fetch(`${API_URL}/auth/users`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to get users');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Add a new user (admin only)
 * @param {string} email The new user's email
 * @param {string} password The new user's password
 * @param {string} role The new user's role ('admin', 'read_write', or 'read')
 * @returns {Promise<Object>} Result of the user creation
 */
export const addUser = async (email, password, role = 'read') => {
  try {
    const response = await fetch(`${API_URL}/auth/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password, role }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to add user');
    }
    
    return data;
  } catch (error) {
    console.error('Error adding user:', error);
    throw error;
  }
};

/**
 * Update a user's password (admin only)
 * @param {string} email The user's email
 * @param {string} password The new password
 * @returns {Promise<Object>} Result of the update
 */
export const updateUserPassword = async (email, password) => {
  try {
    const response = await fetch(`${API_URL}/auth/users/${encodeURIComponent(email)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update user');
    }
    
    return data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Delete a user (admin only)
 * @param {string} email The user's email
 * @returns {Promise<Object>} Result of the deletion
 */
export const deleteUser = async (email) => {
  try {
    const response = await fetch(`${API_URL}/auth/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete user');
    }
    
    return data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};